import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const TutorialContext = createContext(null);

const TUTORIAL_STATE_KEY = 'mx_tutorial_state';

const saveTutorialState = (state) => {
  try {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(TUTORIAL_STATE_KEY, JSON.stringify(state));
    }
  } catch (_) {
    // ignore
  }
};

const loadTutorialState = () => {
  try {
    if (typeof window !== 'undefined') {
      const raw = window.localStorage.getItem(TUTORIAL_STATE_KEY);
      if (raw) return JSON.parse(raw);
    }
  } catch (_) {
    // ignore
  }
  return null;
};

const clearTutorialState = () => {
  try {
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(TUTORIAL_STATE_KEY);
    }
  } catch (_) {
    // ignore
  }
};

export const TutorialProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [tutorialConfig, setTutorialConfig] = useState(null);
  const [mode, setMode] = useState('first-time'); // 'first-time' | 'replay'
  const [targetRect, setTargetRect] = useState(null);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [showCelebration, setShowCelebration] = useState(false);
  const [showSimulatedGame, setShowSimulatedGame] = useState(false);
  const [showSimulatedModal, setShowSimulatedModal] = useState(false);
  const [showSimulatedInbox, setShowSimulatedInbox] = useState(false);
  const [showSimulatedWallet, setShowSimulatedWallet] = useState(false);
  const [walletPhase, setWalletPhase] = useState('buy');
  const [showSimulatedMarketPurchase, setShowSimulatedMarketPurchase] = useState(false);
  const [marketPurchasePhase, setMarketPurchasePhase] = useState('browse');
  const [showSimulatedBsCalculator, setShowSimulatedBsCalculator] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);
  const [promptData, setPromptData] = useState(null);

  const targetRef = useRef(null);
  const pendingNavigateRef = useRef(null);

  // Restore state on mount
  useEffect(() => {
    const saved = loadTutorialState();
    if (saved && saved.isActive && saved.tutorialConfig) {
      setIsActive(true);
      setCurrentStep(saved.currentStep || 0);
      setTutorialConfig(saved.tutorialConfig);
      setMode(saved.mode || 'first-time');
    }
  }, []);

  // Save state on changes
  useEffect(() => {
    if (isActive) {
      saveTutorialState({
        isActive,
        currentStep,
        tutorialConfig,
        mode
      });
    } else {
      clearTutorialState();
    }
  }, [isActive, currentStep, tutorialConfig, mode]);

  const findTargetElement = useCallback((targetId) => {
    if (!targetId) return null;
    // Try data attribute selector
    let el = document.querySelector(`[data-tutorial-target="${targetId}"]`);
    if (el) return el;
    // Fallback: try id
    el = document.getElementById(targetId);
    return el;
  }, []);

  const computeTargetRect = useCallback((element) => {
    if (!element) return null;
    const rect = element.getBoundingClientRect();
    const padding = 8;
    return {
      top: rect.top - padding,
      left: rect.left - padding,
      width: rect.width + padding * 2,
      height: rect.height + padding * 2,
      bottom: rect.bottom + padding,
      right: rect.right + padding,
      centerX: rect.left + rect.width / 2,
      centerY: rect.top + rect.height / 2,
    };
  }, []);

  const updateTargetRect = useCallback(() => {
    if (!isActive || !tutorialConfig) return;
    const step = tutorialConfig.steps[currentStep];
    if (!step || !step.target) {
      setTargetRect(null);
      return;
    }
    const el = findTargetElement(step.target);
    if (el) {
      targetRef.current = el;
      setTargetRect(computeTargetRect(el));
    } else {
      setTargetRect(null);
    }
  }, [isActive, tutorialConfig, currentStep, findTargetElement, computeTargetRect]);

  // Recompute on scroll/resize/step change
  useEffect(() => {
    if (!isActive) return;
    updateTargetRect();
    const onScroll = () => updateTargetRect();
    const onResize = () => updateTargetRect();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [isActive, updateTargetRect]);

  // Navigation handling
  useEffect(() => {
    if (pendingNavigation && isActive && tutorialConfig) {
      const step = tutorialConfig.steps[currentStep];
      if (step?.navigateTo && location.pathname !== step.navigateTo) {
        pendingNavigateRef.current = step.navigateTo;
        navigate(step.navigateTo);
      }
      setPendingNavigation(null);
    }
  }, [pendingNavigation, isActive, tutorialConfig, currentStep, location.pathname, navigate]);

  const startTutorial = useCallback((config, tutorialMode = 'first-time') => {
    setTutorialConfig(config);
    setMode(tutorialMode);
    setCurrentStep(0);
    setIsActive(true);
    setShowCelebration(false);
    setShowSimulatedGame(false);
    setShowSimulatedModal(false);
    setShowSimulatedInbox(false);
    setShowSimulatedWallet(false);
    setShowSimulatedMarketPurchase(false);
    setMarketPurchasePhase('browse');
    setShowSimulatedBsCalculator(false);
    setShowPrompt(false);
    setPromptData(null);
  }, []);

  const nextStep = useCallback(() => {
    if (!tutorialConfig) return;
    setCurrentStep(prev => {
      const next = prev + 1;
      if (next >= tutorialConfig.steps.length) {
        // Tutorial complete - mark in localStorage then clear
        if (tutorialConfig?.id) {
          try {
            const raw = window.localStorage.getItem('mx_tutorials_completed');
            const arr = raw ? JSON.parse(raw) : [];
            if (!arr.includes(tutorialConfig.id)) {
              arr.push(tutorialConfig.id);
              window.localStorage.setItem('mx_tutorials_completed', JSON.stringify(arr));
            }
          } catch (_) {}
        }
        setIsActive(false);
        setShowCelebration(false);
        setShowSimulatedGame(false);
        setShowSimulatedModal(false);
        setShowSimulatedInbox(false);
        setShowSimulatedWallet(false);
        setShowSimulatedMarketPurchase(false);
        setMarketPurchasePhase('browse');
        clearTutorialState();
        // After onboarding bingo tutorial, redirect to bingo lobby so user can play for real
        if (tutorialConfig?.id === 'onboarding-v1') {
          navigate('/bingo');
        }
        return prev;
      }
      const step = tutorialConfig.steps[next];
      setTargetRect(null);

      // Clear simulated states that are not needed by the new step
      if (step.action !== 'simulate-inbox') setShowSimulatedInbox(false);
      if (step.action !== 'simulate-modal') setShowSimulatedModal(false);
      if (step.action !== 'simulate-game') setShowSimulatedGame(false);
      if (step.action !== 'simulate-wallet') setShowSimulatedWallet(false);
      if (step.action !== 'simulate-market') {
        setShowSimulatedMarketPurchase(false);
        setMarketPurchasePhase('browse');
      }
      if (step.action !== 'simulate-bcv-calculator') setShowSimulatedBsCalculator(false);
      if (step.action !== 'celebration') setShowCelebration(false);

      if (step.action === 'navigate' && step.navigateTo) {
        // Save state explicitly before navigating so the new page loads with correct step
        saveTutorialState({
          isActive: true,
          currentStep: next,
          tutorialConfig,
          mode,
        });
        setPendingNavigation(Date.now());
      }
      if (step.action === 'simulate-inbox') {
        setShowSimulatedInbox(true);
      }
      if (step.action === 'simulate-modal') {
        setShowSimulatedModal(true);
      }
      if (step.action === 'simulate-game') {
        setShowSimulatedGame(true);
      }
      if (step.action === 'simulate-wallet') {
        setShowSimulatedWallet(true);
        setWalletPhase(step.phase || 'buy');
      }
      if (step.action === 'simulate-market') {
        setShowSimulatedMarketPurchase(true);
        setMarketPurchasePhase(step.phase || 'browse');
      }
      if (step.action === 'simulate-bcv-calculator') {
        setShowSimulatedBsCalculator(true);
      }
      if (step.action === 'celebration') {
        setShowCelebration(true);
      }
      return next;
    });
  }, [tutorialConfig]);

  const prevStep = useCallback(() => {
    setCurrentStep(prev => {
      if (prev > 0) {
        setTargetRect(null);
        return prev - 1;
      }
      return prev;
    });
  }, []);

  const skipTutorial = useCallback(() => {
    setIsActive(false);
    setShowCelebration(false);
    setShowSimulatedGame(false);
    setShowSimulatedModal(false);
    setShowSimulatedInbox(false);
    setShowSimulatedWallet(false);
    setShowSimulatedMarketPurchase(false);
    setMarketPurchasePhase('browse');
    setShowSimulatedBsCalculator(false);
    clearTutorialState();
    // Mark as skipped in session so we don't prompt again this session
    try {
      if (typeof window !== 'undefined') {
        window.sessionStorage.setItem('mx_tutorial_skipped', '1');
      }
    } catch (_) {
      // ignore
    }
  }, []);

  const completeTutorial = useCallback(() => {
    // Mark as completed in localStorage
    if (tutorialConfig?.id) {
      try {
        const raw = window.localStorage.getItem('mx_tutorials_completed');
        const arr = raw ? JSON.parse(raw) : [];
        if (!arr.includes(tutorialConfig.id)) {
          arr.push(tutorialConfig.id);
          window.localStorage.setItem('mx_tutorials_completed', JSON.stringify(arr));
        }
      } catch (_) {
        // ignore
      }
    }
    setIsActive(false);
    setShowCelebration(false);
    setShowSimulatedGame(false);
    setShowSimulatedModal(false);
    setShowSimulatedInbox(false);
    setShowSimulatedWallet(false);
    setShowSimulatedMarketPurchase(false);
    setMarketPurchasePhase('browse');
    setShowSimulatedBsCalculator(false);
    clearTutorialState();
  }, [tutorialConfig]);

  const advanceAfterAction = useCallback(() => {
    nextStep();
  }, [nextStep]);

  const showTutorialPrompt = useCallback((data) => {
    setPromptData(data);
    setShowPrompt(true);
  }, []);

  const hideTutorialPrompt = useCallback(() => {
    setShowPrompt(false);
    setPromptData(null);
  }, []);

  const value = {
    isActive,
    currentStep,
    totalSteps: tutorialConfig?.steps?.length || 0,
    tutorialId: tutorialConfig?.id || null,
    mode,
    tutorialConfig,
    targetRect,
    showCelebration,
    showSimulatedGame,
    showSimulatedModal,
    showSimulatedInbox,
    showSimulatedWallet,
    walletPhase,
    showSimulatedMarketPurchase,
    marketPurchasePhase,
    showSimulatedBsCalculator,
    showPrompt,
    promptData,
    startTutorial,
    nextStep,
    prevStep,
    skipTutorial,
    completeTutorial,
    advanceAfterAction,
    updateTargetRect,
    findTargetElement,
    showTutorialPrompt,
    hideTutorialPrompt,
    setMarketPurchasePhase,
  };

  return (
    <TutorialContext.Provider value={value}>
      {children}
    </TutorialContext.Provider>
  );
};

export const useTutorial = () => {
  const context = useContext(TutorialContext);
  if (!context) {
    throw new Error('useTutorial must be used within TutorialProvider');
  }
  return context;
};
